import React, { useCallback, useEffect, useState } from 'react';
import {
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  Badge,
  Divider,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import BarChartIcon from '@material-ui/icons/BarChart';
import PersonIcon from '@material-ui/icons/Person';
import { useApi } from '@backstage/core-plugin-api';
import { useTechDocsReaderPage } from '@backstage/plugin-techdocs-react';
import { techdocsViewsApiRef } from './plugin';

const useStyles = makeStyles(theme => ({
  popover: {
    padding: theme.spacing(2),
    minWidth: 320,
    maxWidth: 400,
    maxHeight: 480,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: 14,
    border: '2px solid #fff',
  },
  listItem: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  daysAgo: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  iconButton: {
    color: theme.palette.text.secondary,
    padding: 6,
  },
}));

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
}

const AVATAR_COLORS = [
  '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2',
  '#c2185b', '#0097a7', '#f57c00', '#455a64',
  '#5d4037', '#00838f', '#6a1b9a', '#2e7d32',
  '#ef6c00', '#283593', '#ad1457', '#00695c',
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(p => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

interface Visitor {
  user_ref: string;
  user_display_name: string;
  first_visited_at: string;
  last_visited_at?: string;
}

export default function TechDocsViewsAddon() {
  const classes = useStyles();
  const api = useApi(techdocsViewsApiRef);
  const { entityRef } = useTechDocsReaderPage();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState(false);
  const [recorded, setRecorded] = useState(false);

  const entityRefStr =
    typeof entityRef === 'string'
      ? entityRef
      : `${entityRef.kind}:${entityRef.namespace ?? 'default'}/${entityRef.name}`;

  // Record visit once
  useEffect(() => {
    if (!recorded && entityRefStr) {
      api.recordVisit(entityRefStr).catch(() => {});
      setRecorded(true);
    }
  }, [api, entityRefStr, recorded]);

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      setLoading(true);
      api
        .getVisitors(entityRefStr)
        .then(data => setVisitors(data.visitors))
        .catch(() => setVisitors([]))
        .finally(() => setLoading(false));
    },
    [api, entityRefStr],
  );

  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        className={classes.iconButton}
        onClick={handleOpen}
        size="small"
        title="View visitors"
      >
        <Badge
          badgeContent={visitors.length || undefined}
          color="primary"
          max={99}
        >
          <BarChartIcon fontSize="small" />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box className={classes.popover}>
          <Box className={classes.header}>
            <Typography variant="subtitle1">
              <strong>Visitors</strong>
            </Typography>
            <Typography variant="body2" className={classes.daysAgo}>
              {visitors.length} unique
            </Typography>
          </Box>
          <Divider />

          {loading && (
            <Box textAlign="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!loading && visitors.length === 0 && (
            <Typography className={classes.emptyState}>
              No visitors yet
            </Typography>
          )}

          {!loading && visitors.length > 0 && (
            <List dense disablePadding>
              {visitors.map(v => (
                <ListItem key={v.user_ref} className={classes.listItem}>
                  <ListItemAvatar>
                    <Avatar className={classes.avatar} style={{ backgroundColor: getAvatarColor(v.user_display_name || v.user_ref) }}>
                      {v.user_display_name
                        ? getInitials(v.user_display_name)
                        : <PersonIcon fontSize="small" />
                      }
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={(v.user_display_name || v.user_ref).split('@')[0]}
                    secondary={timeAgo(v.last_visited_at || v.first_visited_at)}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
}
